import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
   ResponsiveContainer,
   LineChart,
   Line,
   BarChart,
   Bar,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   Legend,
} from 'recharts';
import Loader from '../../components/Loader';
import Button from '../../components/common/Button';
import SearchableSelect from '../../components/common/SearchableSelect';
import { toolUsageService } from '../../services/toolUsageService';

const PRESETS = ['Today', '7 Days', '30 Days', 'Custom'];
const OPENS_COLOR = '#2563eb';
const USES_COLOR = '#16a34a';
const TOP_N = 10;

// Days roll over at midnight IST, matching how the backend buckets events.
const istDate = (offsetDays = 0) => {
   const d = new Date(Date.now() - offsetDays * 86400000);
   return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const getRange = (preset, customFrom, customTo) => {
   if (preset === 'Today') return { from: istDate(), to: istDate() };
   if (preset === '7 Days') return { from: istDate(6), to: istDate() };
   if (preset === '30 Days') return { from: istDate(29), to: istDate() };
   return { from: customFrom, to: customTo };
};

const fmtDate = (d) => {
   if (!d) return '';
   const [, m, day] = d.split('-');
   return `${day}/${m}`;
};

const ACRONYMS = new Set([
   'json',
   'uuid',
   'jwt',
   'px',
   'rem',
   'em',
   'vw',
   'utm',
   'html',
   'cron',
   'emi',
]);

const toolLabel = (slug) =>
   slug
      .split('-')
      .map((w) => (ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');

const thClass =
   'px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';

const ChartCard = ({ title, subtitle, children }) => (
   <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-6">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
      <div className="mt-4">{children}</div>
   </div>
);

const EmptyChart = () => (
   <div className="flex items-center justify-center h-[260px] text-sm text-gray-400 dark:text-gray-600">
      No data for this period
   </div>
);

const Kpi = ({ label, value, color }) => (
   <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md p-4">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
         {label}
      </p>
      <p className={`mt-1 text-2xl font-bold ${color || 'text-gray-900 dark:text-gray-100'}`}>
         {value}
      </p>
   </div>
);

// `refreshKey` changes when the dashboard's Refresh button is pressed, forcing a reload.
// `allTime` is the dashboard's cached all-time totals ({ opens, uses, used }).
const ToolUsagePanel = ({ refreshKey = 0, allTime, loading: statsLoading = false }) => {
   const [preset, setPreset] = useState('7 Days');
   const [customFrom, setCustomFrom] = useState(istDate());
   const [customTo, setCustomTo] = useState(istDate());
   const [range, setRange] = useState(() => getRange('7 Days'));
   const [tool, setTool] = useState('all');
   const [rows, setRows] = useState([]);
   const [daily, setDaily] = useState([]);
   const [loading, setLoading] = useState(true);
   const [dailyLoading, setDailyLoading] = useState(true);
   const [toolTotals, setToolTotals] = useState(null);

   const fetchSummary = useCallback(async (r) => {
      try {
         setLoading(true);
         const res = await toolUsageService.getSummary(r);
         setRows(res.data);
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to load tool usage');
      } finally {
         setLoading(false);
      }
   }, []);

   const fetchDaily = useCallback(async (selectedTool, r) => {
      try {
         setDailyLoading(true);
         const res = await toolUsageService.getDaily({
            tool: selectedTool === 'all' ? undefined : selectedTool,
            ...r,
         });
         setDaily(res.data);
      } catch (error) {
         toast.error(error.response?.data?.message || 'Failed to load daily usage');
      } finally {
         setDailyLoading(false);
      }
   }, []);

   useEffect(() => {
      fetchSummary(range);
   }, [range, refreshKey, fetchSummary]);

   // All-time numbers for the selected tool (the cached dashboard totals cover all tools only).
   useEffect(() => {
      if (tool === 'all') {
         setToolTotals(null);
         return;
      }
      let cancelled = false;
      setToolTotals(null);
      toolUsageService
         .getTotals(tool)
         .then((res) => !cancelled && setToolTotals(res.data))
         .catch((error) =>
            toast.error(error.response?.data?.message || 'Failed to load tool totals')
         );
      return () => {
         cancelled = true;
      };
   }, [tool, refreshKey]);

   useEffect(() => {
      fetchDaily(tool, range);
   }, [tool, range, refreshKey, fetchDaily]);

   const applyPreset = (p) => {
      setPreset(p);
      if (p !== 'Custom') setRange(getRange(p));
   };

   const applyCustom = () => {
      if (!customFrom || !customTo || customFrom > customTo) {
         toast.error('Choose a valid date range');
         return;
      }
      setRange({ from: customFrom, to: customTo });
   };

   const toolOptions = useMemo(
      () => [
         { id: 'all', name: 'All tools' },
         ...rows
            .map((r) => ({ id: r.tool, name: toolLabel(r.tool) }))
            .sort((a, b) => a.name.localeCompare(b.name)),
      ],
      [rows]
   );

   // Local search so typing narrows the list of tools.
   const searchTools = useCallback(
      async (query) => {
         const q = query.trim().toLowerCase();
         return toolOptions.filter((o) => o.name.toLowerCase().includes(q));
      },
      [toolOptions]
   );

   // All tools: the cached dashboard totals. One tool: that tool's own totals (0 while loading).
   const allTimeStats =
      tool === 'all'
         ? allTime && {
              opens: statsLoading ? 0 : allTime.opens,
              uses: statsLoading ? 0 : allTime.uses,
              extra: statsLoading ? 0 : allTime.used,
           }
         : {
              opens: toolTotals?.opens ?? 0,
              uses: toolTotals?.uses ?? 0,
              extra: toolTotals?.days ?? 0,
           };

   const visibleRows = tool === 'all' ? rows : rows.filter((r) => r.tool === tool);
   const totals = visibleRows.reduce(
      (acc, r) => ({ opens: acc.opens + r.opens, uses: acc.uses + r.uses }),
      { opens: 0, uses: 0 }
   );
   const useRate = totals.opens > 0 ? Math.round((totals.uses / totals.opens) * 1000) / 10 : 0;

   const topTools = rows
      .filter((r) => r.opens > 0 || r.uses > 0)
      .slice(0, TOP_N)
      .map((r) => ({ name: toolLabel(r.tool), opens: r.opens, uses: r.uses }));

   const hasDailyData = daily.some((d) => d.opens > 0 || d.uses > 0);
   const scopeLabel = tool === 'all' ? 'All tools' : toolLabel(tool);

   return (
      <div className="space-y-6">
         <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
               Opens and uses per tool, by day (IST).
            </p>
            <div className="flex items-center gap-3 w-full sm:w-auto">
               <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tool</span>
               <div className="flex-1 sm:flex-none sm:w-64">
                  <SearchableSelect
                     aria-label="Filter by tool"
                     options={toolOptions}
                     value={tool}
                     onChange={(value) => setTool(value || 'all')}
                     onSearch={searchTools}
                     loadOnOpen
                     minSearchLength={0}
                     placeholder="All tools"
                     className="!mb-0"
                  />
               </div>
            </div>
         </div>

         <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((p) => (
               <button
                  key={p}
                  onClick={() => applyPreset(p)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                     preset === p
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
               >
                  {p}
               </button>
            ))}
            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
               {range.from === range.to ? range.from : `${range.from} to ${range.to}`}
            </span>
         </div>

         {preset === 'Custom' && (
            <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
               <span className="text-xs text-gray-500 dark:text-gray-400">From</span>
               <input
                  type="date"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
               />
               <span className="text-xs text-gray-500 dark:text-gray-400">To</span>
               <input
                  type="date"
                  value={customTo}
                  min={customFrom}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
               />
               <Button size="sm" onClick={applyCustom}>
                  Apply
               </Button>
            </div>
         )}

         {allTimeStats && (
            <div>
               <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                  All time
               </p>
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Kpi
                     label="Total opens"
                     value={allTimeStats.opens}
                     color="text-blue-600 dark:text-blue-400"
                  />
                  <Kpi
                     label="Total uses"
                     value={allTimeStats.uses}
                     color="text-green-600 dark:text-green-400"
                  />
                  <Kpi
                     label={tool === 'all' ? 'Tools used' : 'Days active'}
                     value={allTimeStats.extra}
                  />
               </div>
            </div>
         )}

         <div>
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
               Selected period
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
               <Kpi label="Opens" value={totals.opens} color="text-blue-600 dark:text-blue-400" />
               <Kpi label="Uses" value={totals.uses} color="text-green-600 dark:text-green-400" />
               <Kpi label="Use rate" value={`${useRate}%`} />
            </div>
         </div>

         <ChartCard title={`Daily trend - ${scopeLabel}`} subtitle="Opens and uses per day">
            {dailyLoading ? (
               <Loader />
            ) : !hasDailyData ? (
               <EmptyChart />
            ) : (
               <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={daily} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                     <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                     <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={fmtDate} />
                     <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                     <Tooltip labelFormatter={fmtDate} />
                     <Legend />
                     <Line
                        type="monotone"
                        dataKey="opens"
                        name="Opens"
                        stroke={OPENS_COLOR}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                     />
                     <Line
                        type="monotone"
                        dataKey="uses"
                        name="Uses"
                        stroke={USES_COLOR}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                     />
                  </LineChart>
               </ResponsiveContainer>
            )}
         </ChartCard>

         {tool === 'all' && (
            <ChartCard
               title={`Top ${TOP_N} tools`}
               subtitle="Opens vs uses for the selected period, ranked by uses"
            >
               {loading ? (
                  <Loader />
               ) : topTools.length === 0 ? (
                  <EmptyChart />
               ) : (
                  <ResponsiveContainer width="100%" height={Math.max(220, topTools.length * 44)}>
                     <BarChart
                        data={topTools}
                        layout="vertical"
                        margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                     >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
                        <Tooltip cursor={{ fill: 'rgba(148, 163, 184, 0.15)' }} />
                        <Legend />
                        <Bar
                           dataKey="opens"
                           name="Opens"
                           fill={OPENS_COLOR}
                           radius={[0, 3, 3, 0]}
                        />
                        <Bar dataKey="uses" name="Uses" fill={USES_COLOR} radius={[0, 3, 3, 0]} />
                     </BarChart>
                  </ResponsiveContainer>
               )}
            </ChartCard>
         )}

         {loading ? (
            <Loader />
         ) : (
            <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
               <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                     <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                           <th className={`${thClass} text-left`}>Tool</th>
                           <th className={`${thClass} text-right`}>Opens</th>
                           <th className={`${thClass} text-right`}>Uses</th>
                           <th className={`${thClass} text-right`}>Use rate</th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {visibleRows.map((r) => (
                           <tr
                              key={r.tool}
                              onClick={() => setTool(tool === r.tool ? 'all' : r.tool)}
                              className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                 tool === r.tool ? 'bg-blue-50 dark:bg-gray-800' : ''
                              }`}
                           >
                              <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                                 {toolLabel(r.tool)}
                              </td>
                              <td className="px-6 py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                                 {r.opens}
                              </td>
                              <td className="px-6 py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                                 {r.uses}
                              </td>
                              <td className="px-6 py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                                 {r.useRate}%
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            </div>
         )}
      </div>
   );
};

export default ToolUsagePanel;
